import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import {
  listPlacementSessions,
  getAnalyticsOverview,
  getAnalyticsCampaigns,
  getAnalyticsStudents,
} from "../api/client";

// Results & Analytics — read-only aggregates over assignment status/score for
// the whole institution or one batch. Every number comes from the tenant-scoped
// /api/institution/analytics/* endpoints (no student transcripts are exposed).

const STUDENT_PAGE_SIZE = 25;

const selectClasses =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

// Bar colors mirror the assignment status pills used elsewhere in the portal.
const STATUS_META = {
  assigned: { label: "Assigned", bar: "bg-primary" },
  started: { label: "Started", bar: "bg-warning" },
  completed: { label: "Completed", bar: "bg-success" },
  expired: { label: "Expired", bar: "bg-muted-foreground/50" },
  cancelled: { label: "Cancelled", bar: "bg-muted-foreground/30" },
};

const pctText = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);
const scoreText = (s) => (s == null ? "—" : Number(s).toFixed(1));

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function typeLabel(v) {
  return v ? String(v).replace(/_/g, " ") : "—";
}

// Per-student trend arrow for the rollup table (backend classifies first→latest
// completed score into improving / declining / flat / na).
const TREND_ARROW = {
  improving: { icon: "TrendingUp", cls: "text-success" },
  declining: { icon: "TrendingDown", cls: "text-destructive" },
  flat: { icon: "Minus", cls: "text-muted-foreground" },
  na: { icon: "Minus", cls: "text-muted-foreground/40" },
};

function StudentTrend({ trend, delta }) {
  const meta = TREND_ARROW[trend] || TREND_ARROW.na;
  const showDelta = trend === "improving" || trend === "declining";
  return (
    <span
      className={"inline-flex items-center justify-end gap-1 text-xs font-medium " + meta.cls}
      title={trend === "na" ? "Needs 2+ completed interviews" : `${delta > 0 ? "+" : ""}${delta} pts first → latest`}
    >
      <Icon name={meta.icon} size={14} />
      {showDelta ? `${delta > 0 ? "+" : ""}${delta}` : null}
    </span>
  );
}

function KpiCard({ icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function StatusBreakdown({ rows, total }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h2 className="text-base font-semibold text-foreground">Status breakdown</h2>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Where every assignment stands right now.
      </p>
      <div className="mt-5 space-y-3">
        {rows.map((r) => {
          const meta = STATUS_META[r.status] || { label: r.status, bar: "bg-muted-foreground/40" };
          const width = total > 0 ? Math.round((r.count / total) * 100) : 0;
          return (
            <div key={r.status} className="flex items-center gap-3">
              <span className="w-24 flex-shrink-0 text-sm text-muted-foreground">
                {meta.label}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <div
                  className={"h-full rounded-full " + meta.bar}
                  style={{ width: `${r.count > 0 ? Math.max(width, 3) : 0}%` }}
                />
              </div>
              <span className="w-10 flex-shrink-0 text-right text-sm font-medium text-foreground">
                {r.count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ScoreDistribution({ buckets }) {
  const maxCount = buckets.reduce((m, b) => Math.max(m, b.count), 0);
  const anyScores = maxCount > 0;
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <h2 className="text-base font-semibold text-foreground">Score distribution</h2>
      <p className="mt-0.5 text-sm text-muted-foreground">
        Completed interviews by score band.
      </p>
      {anyScores ? (
        <div className="mt-5 flex h-40 items-end gap-3">
          {buckets.map((b) => {
            const h = maxCount > 0 ? Math.max((b.count / maxCount) * 100, b.count > 0 ? 4 : 0) : 0;
            return (
              <div key={b.bucket} className="flex flex-1 flex-col items-center gap-2">
                <span className="text-xs font-medium text-foreground">{b.count}</span>
                <div className="flex w-full flex-1 items-end">
                  <div
                    className="w-full rounded-t-md bg-primary/80"
                    style={{ height: `${h}%` }}
                  />
                </div>
                <span className="text-[11px] text-muted-foreground">{b.bucket}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-5 flex h-40 items-center justify-center text-sm text-muted-foreground">
          No completed interviews yet.
        </div>
      )}
    </div>
  );
}

export default function Results() {
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [batchFilter, setBatchFilter] = useState("");

  const [overview, setOverview] = useState(null);
  const [campaignPerf, setCampaignPerf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [students, setStudents] = useState([]);
  const [studentTotal, setStudentTotal] = useState(0);
  const [studentPage, setStudentPage] = useState(0);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const rows = await listPlacementSessions();
        setBatches(Array.isArray(rows) ? rows : []);
      } catch {
        /* the batch picker just stays at "All batches" */
      }
    })();
  }, []);

  const scopeParams = useMemo(
    () => (batchFilter ? { placementSessionId: batchFilter } : {}),
    [batchFilter]
  );

  const loadMain = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ov, cp] = await Promise.all([
        getAnalyticsOverview(scopeParams),
        getAnalyticsCampaigns(scopeParams),
      ]);
      setOverview(ov);
      setCampaignPerf(Array.isArray(cp) ? cp : []);
    } catch (err) {
      setError(err?.error || "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [scopeParams]);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    setStudentsError("");
    try {
      const data = await getAnalyticsStudents({
        ...scopeParams,
        limit: STUDENT_PAGE_SIZE,
        offset: studentPage * STUDENT_PAGE_SIZE,
      });
      setStudents(data.students || []);
      setStudentTotal(data.total || 0);
    } catch (err) {
      setStudentsError(err?.error || "Could not load student results.");
    } finally {
      setStudentsLoading(false);
    }
  }, [scopeParams, studentPage]);

  useEffect(() => {
    loadMain();
  }, [loadMain]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Reset student paging when the batch scope changes.
  useEffect(() => {
    setStudentPage(0);
  }, [batchFilter]);

  const kpis = overview?.kpis || null;
  const statusRows = overview?.statusBreakdown || [];
  const buckets = overview?.scoreDistribution || [];
  const totalAssignments = kpis?.totalAssignments ?? 0;
  const attempted = (kpis?.totalAssignments ?? 0) - (kpis?.cancelled ?? 0);
  const studentPages = Math.max(1, Math.ceil(studentTotal / STUDENT_PAGE_SIZE));

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header + batch scope */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Results &amp; Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {kpis
              ? `${kpis.totalStudents} student${kpis.totalStudents === 1 ? "" : "s"} · ${kpis.totalCampaigns} campaign${kpis.totalCampaigns === 1 ? "" : "s"} in scope`
              : "Completion and score insights across your cohort."}
          </p>
        </div>
        <select
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          className={selectClasses + " sm:w-56"}
        >
          <option value="">All batches</option>
          {batches.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </div>

      {/* Main load error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
          <button onClick={loadMain} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-xl border border-border bg-muted/40" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="h-56 animate-pulse rounded-xl border border-border bg-muted/40" />
            <div className="h-56 animate-pulse rounded-xl border border-border bg-muted/40" />
          </div>
        </div>
      ) : (
        !error && (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                icon="CheckCircle2"
                label="Completion rate"
                value={pctText(kpis?.completionRate)}
                hint={`${kpis?.completed ?? 0} of ${attempted} attempted`}
              />
              <KpiCard
                icon="Gauge"
                label="Average score"
                value={kpis?.averageScore == null ? "—" : scoreText(kpis.averageScore)}
                hint="Across completed interviews"
              />
              <KpiCard
                icon="Target"
                label="Pass rate"
                value={pctText(kpis?.passRate)}
                hint={`Score ≥ ${kpis?.passThreshold ?? 60}`}
              />
              <KpiCard
                icon="Send"
                label="Completed"
                value={kpis?.completed ?? 0}
                hint={`${kpis?.inProgress ?? 0} in progress`}
              />
            </div>

            {totalAssignments === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon name="BarChart3" size={24} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground">No results yet</h3>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  Once you assign a campaign and students start completing interviews,
                  their completion and scores will show up here.
                </p>
                <button
                  onClick={() => navigate("/institution/campaigns")}
                  className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                >
                  Go to campaigns <Icon name="ArrowRight" size={14} />
                </button>
              </div>
            ) : (
              <>
                {/* Charts */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <StatusBreakdown rows={statusRows} total={totalAssignments} />
                  <ScoreDistribution buckets={buckets} />
                </div>

                {/* Campaign performance */}
                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <h2 className="text-base font-semibold text-foreground">Campaign performance</h2>
                    <span className="text-xs text-muted-foreground">
                      {campaignPerf.length} campaign{campaignPerf.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  {campaignPerf.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                      No campaigns in this scope yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-5 py-3 font-medium">Campaign</th>
                            <th className="px-3 py-3 font-medium">Batch</th>
                            <th className="px-3 py-3 font-medium">Type</th>
                            <th className="px-3 py-3 text-right font-medium">Assigned</th>
                            <th className="px-3 py-3 text-right font-medium">Completed</th>
                            <th className="px-3 py-3 text-right font-medium">In progress</th>
                            <th className="px-3 py-3 text-right font-medium">Avg score</th>
                            <th className="px-5 py-3 text-right font-medium">Completion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {campaignPerf.map((c) => (
                            <tr
                              key={c.campaignId}
                              onClick={() => navigate(`/institution/campaigns/${c.campaignId}`)}
                              className="cursor-pointer border-t border-border transition-colors hover:bg-muted/40"
                            >
                              <td className="px-5 py-3 font-medium text-foreground">{c.name}</td>
                              <td className="px-3 py-3 text-muted-foreground">{c.batchName}</td>
                              <td className="px-3 py-3 capitalize text-muted-foreground">
                                {typeLabel(c.interviewType)}
                              </td>
                              <td className="px-3 py-3 text-right text-foreground">{c.assigned}</td>
                              <td className="px-3 py-3 text-right text-foreground">{c.completed}</td>
                              <td className="px-3 py-3 text-right text-foreground">{c.inProgress}</td>
                              <td className="px-3 py-3 text-right text-foreground">
                                {scoreText(c.averageScore)}
                              </td>
                              <td className="px-5 py-3 text-right text-foreground">
                                {pctText(c.completionRate)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Student rollup */}
                <div className="rounded-xl border border-border bg-card">
                  <div className="flex items-center justify-between border-b border-border px-5 py-4">
                    <h2 className="text-base font-semibold text-foreground">Student results</h2>
                    <span className="text-xs text-muted-foreground">
                      {studentTotal} student{studentTotal === 1 ? "" : "s"}
                    </span>
                  </div>

                  {studentsError ? (
                    <div className="flex items-center gap-2 px-5 py-4 text-sm text-destructive">
                      <Icon name="AlertCircle" size={16} />
                      <span>{studentsError}</span>
                      <button onClick={loadStudents} className="ml-auto font-medium underline">
                        Retry
                      </button>
                    </div>
                  ) : studentsLoading ? (
                    <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                      Loading students…
                    </div>
                  ) : students.length === 0 ? (
                    <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                      No students in this scope yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-5 py-3 font-medium">Student</th>
                            <th className="px-3 py-3 font-medium">Batch</th>
                            <th className="px-3 py-3 text-right font-medium">Assigned</th>
                            <th className="px-3 py-3 text-right font-medium">Completed</th>
                            <th className="px-3 py-3 text-right font-medium">Avg score</th>
                            <th className="px-3 py-3 text-right font-medium">Trend</th>
                            <th className="px-5 py-3 text-right font-medium">Last completed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {students.map((s) => (
                            <tr
                              key={s.studentId}
                              onClick={() => navigate(`/institution/students/${s.studentId}`)}
                              className="cursor-pointer border-t border-border transition-colors hover:bg-muted/40"
                            >
                              <td className="px-5 py-3">
                                <div className="font-medium text-foreground">{s.name}</div>
                                <div className="text-xs text-muted-foreground">{s.email}</div>
                              </td>
                              <td className="px-3 py-3 text-muted-foreground">{s.batchName}</td>
                              <td className="px-3 py-3 text-right text-foreground">{s.assigned}</td>
                              <td className="px-3 py-3 text-right text-foreground">{s.completed}</td>
                              <td className="px-3 py-3 text-right text-foreground">
                                {scoreText(s.averageScore)}
                              </td>
                              <td className="px-3 py-3 text-right">
                                <StudentTrend trend={s.trend} delta={s.trendDelta} />
                              </td>
                              <td className="px-5 py-3 text-right text-muted-foreground">
                                {fmtDateTime(s.lastCompletedAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {!studentsLoading && !studentsError && studentTotal > STUDENT_PAGE_SIZE && (
                    <div className="flex items-center justify-between border-t border-border px-5 py-3 text-sm">
                      <span className="text-muted-foreground">
                        Page {studentPage + 1} of {studentPages}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setStudentPage((p) => Math.max(0, p - 1))}
                          disabled={studentPage === 0}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Icon name="ChevronLeft" size={14} /> Prev
                        </button>
                        <button
                          onClick={() => setStudentPage((p) => Math.min(studentPages - 1, p + 1))}
                          disabled={studentPage >= studentPages - 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Next <Icon name="ChevronRight" size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )
      )}
    </div>
  );
}
